'use client';

/**
 * TextRenderer Component
 *
 * Renders TEXT, TEXTAREA, EMAIL, URL, PHONE field types.
 * Supports display, edit, and inline modes.
 */

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { getNeonStyles, type NeonAccent } from '../../../theme/palette';
import { transitionPresets } from '../../../theme/transitions';
import type { FieldDefinition, FieldMode } from '../../../types/entityFramework';
import { formatPhone } from '../../../utils/formatters';
import { transientOptions } from '../../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface TextRendererProps {
  /** Field definition */
  field: FieldDefinition;
  /** Current value */
  value: unknown;
  /** Render mode */
  mode: FieldMode;
  /** Change handler */
  onChange?: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Neon accent color */
  neonAccent?: NeonAccent;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $hasError: boolean;
  $size: 'small' | 'medium' | 'large';
}

const StyledTextField = styled(
  TextField,
  transientOptions,
)<StyledTextFieldProps>(({ theme, $isDark, $neonAccent, $hasError, $size }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const sizeStyles = {
    small: { fontSize: '0.875rem', padding: '8px 12px' },
    medium: { fontSize: '1rem', padding: '12px 16px' },
    large: { fontSize: '1.125rem', padding: '16px 20px' },
  };

  const s = sizeStyles[$size];

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
      transition: transitionPresets.input,

      '& input, & textarea': {
        fontSize: s.fontSize,
        padding: s.padding,
      },

      '& fieldset': {
        borderColor: $hasError
          ? theme.palette.error.main
          : neonStyles
            ? neonStyles.border
            : $isDark
              ? 'rgba(255, 255, 255, 0.15)'
              : 'rgba(0, 0, 0, 0.15)',
        transition: transitionPresets.input,
      },

      '&:hover fieldset': {
        borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

        '& fieldset': {
          borderColor: $hasError ? theme.palette.error.main : neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 2,
          boxShadow: neonStyles ? `0 0 12px ${neonStyles.glow}` : undefined,
        },
      },

      '&.Mui-disabled': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.04)',
      },
    },

    '& .MuiInputLabel-root': {
      color: $isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
      fontSize: '0.875rem',

      '&.MuiInputLabel-shrink': {
        backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
        padding: '0 6px',
        marginLeft: '-4px',
      },

      '&.Mui-focused': {
        color: neonStyles ? neonStyles.base : theme.palette.primary.main,
      },
    },

    '& .MuiFormHelperText-root': {
      marginLeft: 4,
      marginTop: 6,
    },
  };
});

const DisplayValue = styled(
  Typography,
  transientOptions,
)<{ $isDark: boolean }>(({ $isDark }) => ({
  color: $isDark ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
  wordBreak: 'break-word',
}));

const InlineInput = styled(
  TextField,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}>(({ theme, $isDark, $neonAccent }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: 'transparent',
      transition: transitionPresets.input,

      '& fieldset': {
        borderColor: 'transparent',
      },

      '&:hover fieldset': {
        borderColor: neonStyles
          ? alpha(neonStyles.border, 0.5)
          : $isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
      },

      '&.Mui-focused': {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        '& fieldset': {
          borderColor: neonStyles ? neonStyles.base : theme.palette.primary.main,
          borderWidth: 1,
        },
      },
    },

    '& input': {
      padding: '4px 8px',
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const TextRenderer: React.FC<TextRendererProps> = ({
  field,
  value,
  mode,
  onChange,
  error,
  disabled = false,
  size = 'medium',
  neonAccent,
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const stringValue = value != null ? String(value) : '';

  // Format display value based on field type
  const getDisplayValue = (): React.ReactNode => {
    if (!stringValue) {
      return (
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          -
        </Typography>
      );
    }

    switch (field.fieldType) {
      case 'EMAIL':
        return (
          <Link href={`mailto:${stringValue}`} underline="hover">
            {stringValue}
          </Link>
        );
      case 'URL':
        return (
          <Link href={stringValue} target="_blank" rel="noopener noreferrer" underline="hover">
            {stringValue}
          </Link>
        );
      case 'PHONE':
        return (
          <Link href={`tel:${stringValue}`} underline="hover">
            {formatPhone(stringValue)}
          </Link>
        );
      default:
        return stringValue;
    }
  };

  // Get input type based on field type
  const getInputType = (): string => {
    switch (field.fieldType) {
      case 'EMAIL':
        return 'email';
      case 'URL':
        return 'url';
      case 'PHONE':
        return 'tel';
      default:
        return 'text';
    }
  };

  // Display mode
  if (mode === 'display') {
    return (
      <Box className={className}>
        <DisplayValue variant="body1" $isDark={isDark}>
          {getDisplayValue()}
        </DisplayValue>
      </Box>
    );
  }

  // Inline mode
  if (mode === 'inline') {
    return (
      <InlineInput
        value={stringValue}
        onChange={(e) => onChange?.(e.target.value)}
        type={getInputType()}
        placeholder={field.placeholder}
        disabled={disabled || field.disabled}
        error={!!error}
        size="small"
        fullWidth
        $isDark={isDark}
        $neonAccent={neonAccent}
        className={className}
      />
    );
  }

  // Edit mode
  return (
    <StyledTextField
      value={stringValue}
      onChange={(e) => onChange?.(e.target.value)}
      type={getInputType()}
      label={field.label}
      placeholder={field.placeholder}
      helperText={error || field.helpText}
      error={!!error}
      disabled={disabled || field.disabled}
      required={field.required}
      multiline={field.fieldType === 'TEXTAREA'}
      rows={field.fieldType === 'TEXTAREA' ? 4 : undefined}
      fullWidth
      variant="outlined"
      $isDark={isDark}
      $neonAccent={neonAccent}
      $hasError={!!error}
      $size={size}
      className={className}
    />
  );
};

TextRenderer.displayName = 'TextRenderer';

export default TextRenderer;
